import React, { useState } from "react";
import { Container, Row, Col, Form, Card, Collapse, Button, Spinner } from "react-bootstrap";
import {
  ChevronDown,
  ChevronUp,
  PersonCircle,
  Briefcase,
  FileEarmarkText,
  Gear,
  ShieldCheck,
  CheckCircleFill,
  XCircle,
  Building,
  People,
  Receipt
} from "react-bootstrap-icons";
import TextInput from "components/common/custom/forms/BootTextField/TextField";

// Helper to get icon based on module name
const getModuleIcon = (name) => {
  const lowerName = name.toLowerCase();
  if (lowerName.includes('patient')) return <PersonCircle size={20} />;
  if (lowerName.includes('employee')) return <Briefcase size={20} />;
  if (lowerName.includes('claim')) return <FileEarmarkText size={20} />;
  if (lowerName.includes('permission') || lowerName.includes('role')) return <ShieldCheck size={20} />;
  if (lowerName.includes('branch') || lowerName.includes('office')) return <Building size={20} />;
  if (lowerName.includes('billing')) return <Receipt size={20} />;
  if (lowerName.includes('system') || lowerName.includes('setting')) return <Gear size={20} />;
  return <People size={20} />;
};

const PermissionItem = ({ sub, modIndex, subIndex, onPermissionChange, moduleName }) => {
  const isEnabled = sub.value;

  const handleCardClick = () => {
    onPermissionChange(modIndex, subIndex, !isEnabled);
  };

  return (
    <Col style={{ flex: '0 0 20%', maxWidth: '20%' }} className="mb-2 px-2">
      <div
        className={`permission-item-capsule d-flex align-items-center justify-content-between py-2 px-3 rounded-2 border transition-base ${isEnabled ? 'enabled border-primary-subtle bg-primary-subtle bg-opacity-10' : 'disabled border-translucent bg-opacity-50'}`}
        onClick={handleCardClick}
        style={{ cursor: 'pointer' }}
      >
        <div className="d-flex align-items-center gap-2 overflow-hidden">
          <div className={`status-icon-circle d-flex align-items-center justify-content-center rounded-circle ${isEnabled ? 'bg-primary text-white shadow-sm' : 'bg-secondary bg-opacity-25 text-secondary'}`} style={{ width: '18px', height: '18px', flexShrink: 0 }}>
            {isEnabled ? <CheckCircleFill size={10} /> : <XCircle size={10} />}
          </div>
          <span className={`text-nowrap fs-11 fw-bold ${isEnabled ? 'text-primary' : 'text-500'}`} title={`${sub.name} ${moduleName}`}>
            {sub.name} {moduleName}
          </span>
        </div>
        <Form.Check
          type="switch"
          id={`switch-${modIndex}-${subIndex}`}
          checked={isEnabled}
          onChange={(e) => onPermissionChange(modIndex, subIndex, e.target.checked)}
          className="custom-switch-primary shadow-none ms-2"
          style={{ cursor: 'pointer', transform: 'scale(0.8)', pointerEvents: 'none' }}
        />
      </div>
    </Col>
  );
};

const PermissionGroup = ({ mod, modIndex, onPermissionChange, onMasterChange }) => {
  const [open, setOpen] = useState(true);
  const enabledCount = mod.submodules.filter(s => s.value).length;
  const totalCount = mod.submodules.length;
  const allEnabled = enabledCount === totalCount && totalCount > 0;
  const allDisabled = enabledCount === 0 && totalCount > 0;

  return (
    <Card className="border-0 shadow-sm rounded-3 mb-3 overflow-hidden">
      <Card.Header
        className={`border-0 p-2 d-flex align-items-center justify-content-between transition-base cursor-pointer `}
        onClick={() => setOpen(!open)}
      >
        <div className="d-flex align-items-center gap-3 ms-1">
          <div className={`p-2 rounded-2 d-flex align-items-center justify-content-center shadow-sm ${allEnabled ? 'bg-primary text-white' : 'text-primary border border-primary-subtle'}`}>
            {getModuleIcon(mod.name)}
          </div>
          <div>
            <h6 className="fw-bold mb-0 text-900 fs-10">{mod.name}</h6>
            <p className="mb-0 text-500 fs-11 fw-medium">{enabledCount} of {totalCount} permissions active</p>
          </div>
        </div>

        <div className="d-flex align-items-center gap-4 me-2">
          <div className="d-none d-md-flex align-items-center gap-2">
            <button
              type="button"
              className={`btn btn-link p-0 text-decoration-none fs-11 fw-bold text-uppercase letter-spacing-1 shadow-none transition-base ${allEnabled ? 'text-primary opacity-50 cursor-default' : 'text-primary'}`}
              onClick={(e) => { e.stopPropagation(); if (!allEnabled) onMasterChange(modIndex, true); }}
            >
              Enable All
            </button>
            <span className="text-300 fs-11">|</span>
            <button
              type="button"
              className={`btn btn-link p-0 text-decoration-none fs-11 fw-bold text-uppercase letter-spacing-1 shadow-none transition-base ${allDisabled ? 'text-500 opacity-50 cursor-default' : 'text-500'}`}
              onClick={(e) => { e.stopPropagation(); if (!allDisabled) onMasterChange(modIndex, false); }}
            >
              Disable All
            </button>
          </div>
          <div className="text-400 ms-2">
            {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </div>
        </div>
      </Card.Header>

      <Collapse in={open}>
        <div>
          <Card.Body className="border-top border-translucent pt-3 pb-1 px-2">
            <Row className="gx-0">
              {mod.submodules.map((sub, subIndex) => (
                <PermissionItem
                  key={`${modIndex}-${subIndex}`}
                  sub={sub}
                  modIndex={modIndex}
                  subIndex={subIndex}
                  onPermissionChange={onPermissionChange}
                  moduleName={mod.name}
                />
              ))}
            </Row>
          </Card.Body>
        </div>
      </Collapse>
    </Card>
  );
};

const PermissionTable = ({
  control,
  modules,
  onGlobalPermissionChange,
  onPermissionChange,
  onMasterChange,
}) => {
  return (
    <Container fluid className="px-0 py-2">
      <Row className="mb-3 align-items-end g-3">
        <Col md={4} lg={3}>
          <div className="role-input-group">
            <TextInput
              name="role_name"
              label={<span className="text-secondary fw-bold fs-11">Editing Role Profile</span>}
              disabled={true}
              placeholder="Select Role"
              size="sm"
              control={control}
              className="fw-bold text-primary shadow-none border-0 "
            />
          </div>
        </Col>
        <Col className="d-flex justify-content-end gap-2">
          <Button
            variant="success"
            size="sm"
            className="px-3 fw-bold fs-11 text-uppercase border-0 shadow-sm d-flex align-items-center gap-1"
            onClick={() => onGlobalPermissionChange(true)}
          >
            <ShieldCheck size={14} />
            Activate All Permissions
          </Button>
          <Button
            variant="danger"
            size="sm"
            className="px-3 fw-bold fs-11 text-uppercase border-0 shadow-sm d-flex align-items-center gap-1"
            onClick={() => onGlobalPermissionChange(false)}
          >
            <XCircle size={14} />
            Reset All Permissions
          </Button>
        </Col>
      </Row>

      <div className="permission-groups-container">
        {modules.map((mod, index) => (
          <PermissionGroup
            key={mod.id || index}
            mod={mod}
            modIndex={index}
            onPermissionChange={onPermissionChange}
            onMasterChange={onMasterChange}
          />
        ))}
      </div>

      <style dangerouslySetInnerHTML={{
        __html: `
                .permission-item-capsule {
                    cursor: pointer;
                    min-height: 40px;
                }
                .permission-item-capsule.enabled {
                    background-color: rgba(var(--falcon-primary-rgb), 0.08) !important;
                }
                .permission-item-capsule.enabled:hover {
                    background-color: rgba(var(--falcon-primary-rgb), 0.12) !important;
                }
                .permission-item-capsule.disabled:hover {
                    background-color:  rgba(var(--falcon-primary-rgb), 0.12) !important;
                }
                .transition-base {
                    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
                }
                .custom-switch-primary .form-check-input:checked {
                    background-color: var(--falcon-primary) !important;
                    border-color: var(--falcon-primary) !important;
                }
                .custom-switch-primary .form-check-input {
                    cursor: pointer;
                }
                .letter-spacing-1 {
                    letter-spacing: 0.05em;
                }
                .cursor-pointer {
                    cursor: pointer;
                }
                .border-translucent {
                    border-color: rgba(0, 0, 0, 0.06) !important;
                }
                
                @media (max-width: 1400px) {
                    .permission-groups-container .col {
                        flex: 0 0 25% !important;
                        max-width: 25% !important;
                    }
                }
                @media (max-width: 1200px) {
                    .permission-groups-container .col {
                        flex: 0 0 33.333% !important;
                        max-width: 33.333% !important;
                    }
                }
                @media (max-width: 768px) {
                    .permission-groups-container .col {
                        flex: 0 0 50% !important;
                        max-width: 50% !important;
                    }
                }
                @media (max-width: 576px) {
                    .permission-groups-container .col {
                        flex: 0 0 100% !important;
                        max-width: 100% !important;
                    }
                }
            `}} />
    </Container>
  );
};

export default PermissionTable;
